import React, { useState, useRef, useId, useEffect } from 'react';
import { Upload, Info, Trash2, X, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '@clerk/clerk-react';
import { useLibraryStore, Material } from '../../stores/libraryStore';
import { toast } from 'sonner';
import { useI18n } from '../../i18n';
import { useSwipeClickGuard } from './use-swipe-click-guard';

interface LibraryContextMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export function LibraryContextMenu({ isOpen, onClose }: LibraryContextMenuProps) {
  const { t, isRTL } = useI18n();
  const [swipedItem, setSwipedItem] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSwipedItem(null);
  }, [isOpen, isRTL]);
  
  const { getToken } = useAuth();
  const {
    materials,
    selectMaterial,
    uploadFile,
    pollUploadStatus,
    deleteMaterial,
    isUploading: storeUploading,
  } = useLibraryStore();
  
  // Safe array
  const safeMaterials = Array.isArray(materials) ? materials : [];

  const handleSwipe = (itemId: string, offset: number) => {
    if (isRTL ? offset > 50 : offset < -50) {
      setSwipedItem(itemId);
    } else {
      setSwipedItem(null);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };
  
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setIsUploading(true);
      const token = await getToken();
      if (!token) {
        toast.error(t('library.toast.signInToUpload'));
        return;
      }
      const sessionId = await uploadFile(file, token);
      toast.info(t('library.toast.queued'));
      onClose();
      const material = await pollUploadStatus(sessionId, getToken);
      if (material) selectMaterial(material);
      toast.success(t('library.toast.uploaded'));
    } catch (error) {
      toast.error(t('library.toast.uploadFailedGeneric'));
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleInfo = (item: Material) => {
    const sizeKB = item.file_size ? Math.round(item.file_size / 1024) : 0;
    const sizeMB = (sizeKB / 1024).toFixed(2);
    toast.info(
      `${item.original_filename || item.filename}\n` +
      `${t('library.info.type')}: ${item.file_type || t('common.unknown')}\n` +
      `${t('library.info.size')}: ${sizeKB > 1024 ? sizeMB + ' MB' : sizeKB + ' KB'}\n` +
      `${t('library.info.pages')}: ${item.page_count || t('common.notAvailable')}`
    );
    setSwipedItem(null);
  };

  const handleDelete = async (item: Material) => {
    if (!confirm(t('library.confirmDeleteNamed', { name: item.original_filename || item.filename }))) {
      setSwipedItem(null);
      return;
    }
    
    try {
      setDeletingId(item.id);
      const token = await getToken();
      if (!token) {
        toast.error(t('library.toast.signIn'));
        return;
      }
      await deleteMaterial(item.id, token);
      toast.success(t('library.toast.deleted'));
      setSwipedItem(null);
    } catch (error) {
      toast.error(t('library.toast.deleteFailed'));
      console.error('Delete error:', error);
    } finally {
      setDeletingId(null);
    }
  };
  
  const handleSelectItem = (item: Material) => {
    selectMaterial(item);
    toast.info(t('library.toast.opening', { name: item.original_filename || item.filename }));
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Dimming Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/40 z-50 md:hidden"
            onClick={onClose}
          />

          {/* Context Menu */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed start-4 end-4 top-1/2 -translate-y-1/2 z-50 md:hidden max-h-[80vh] min-h-0 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex min-h-0 max-h-full flex-1 flex-col overflow-hidden rounded-3xl bg-white/95 shadow-2xl backdrop-blur-2xl">
              {/* Header */}
              <div className="flex shrink-0 items-center justify-between px-6 py-4 border-b border-gray-200/50">
                <h2 className="text-xl font-semibold text-[#434343]">{t('library.title')}</h2>
                <button
                  aria-label={t("common.close")}
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200/60 hover:bg-gray-300/60 active:scale-95 transition-all"
                >
                  <X className="h-5 w-5 text-[#434343]" />
                </button>
              </div>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                onChange={handleFileChange}
                className="hidden"
              />

              {/* Upload Button */}
              <div className="shrink-0 px-4 pt-4">
                <button 
                  onClick={handleUploadClick}
                  disabled={isUploading || storeUploading}
                  className="flex w-full items-center gap-3 px-4 py-4 rounded-2xl bg-gradient-to-br from-[#7367FF]/10 to-[#2DD2FE]/10 border border-[#7367FF]/20 hover:from-[#7367FF]/20 hover:to-[#2DD2FE]/20 active:scale-[0.98] transition-all duration-200 disabled:opacity-50"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#7367FF] to-[#2DD2FE] flex items-center justify-center shadow-lg shadow-[#7367FF]/30">
                    {(isUploading || storeUploading) ? (
                      <Loader2 className="h-5 w-5 text-white animate-spin" />
                    ) : (
                    <Upload className="h-5 w-5 text-white" />
                    )}
                  </div>
                  <span className="text-[15px] font-medium text-[#7367FF]">
                    {(isUploading || storeUploading) ? t('common.uploading') : t('library.uploadDocument')}
                  </span>
                </button>
              </div>

              {/* Library Items List */}
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4">
                <div className="space-y-2">
                  {safeMaterials.length === 0 ? (
                    <p className="text-center text-[#434343]/50 py-8">
                      {t('library.empty')}
                    </p>
                  ) : (
                    safeMaterials.map((item) => (
                    <SwipeableRow
                      key={item.id}
                      item={item}
                      isOpen={swipedItem === item.id}
                        isDeleting={deletingId === item.id}
                      onSwipe={(offset) => handleSwipe(item.id, offset)}
                      onInfo={() => handleInfo(item)}
                      onDelete={() => handleDelete(item)}
                        onSelect={() => handleSelectItem(item)}
                        isRTL={isRTL}
                        pagesLabel={t('common.pages')}
                    />
                    ))
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

interface SwipeableRowProps {
  item: Material;
  isOpen: boolean;
  isDeleting?: boolean;
  onSwipe: (offset: number) => void;
  onInfo: () => void;
  onDelete: () => void;
  onSelect: () => void;
  isRTL: boolean;
  pagesLabel: string;
}

function SwipeableRow({ item, isOpen, isDeleting, onSwipe, onInfo, onDelete, onSelect, isRTL, pagesLabel }: SwipeableRowProps) {
  const { t } = useI18n();
  const revealOffset = isRTL ? 148 : -148;
  const swipeClickGuard = useSwipeClickGuard();
  const dragOrigin = useRef(0);
  const foregroundRef = useRef<HTMLButtonElement>(null);
  const actionsId = useId();

  return (
    <div dir={isRTL ? "rtl" : "ltr"} className="relative overflow-hidden rounded-2xl bg-white border border-gray-200/50 shadow-sm"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && isOpen) {
          event.preventDefault();
          onSwipe(0);
          foregroundRef.current?.focus();
        }
      }}
    >
      {/* Swipeable Content */}
      <motion.button
        {...swipeClickGuard}
        ref={foregroundRef}
        type="button"
        disabled={isDeleting}
        aria-expanded={isOpen}
        aria-controls={actionsId}
        aria-keyshortcuts={`${isRTL ? 'ArrowRight' : 'ArrowLeft'} Escape`}
        drag={isDeleting ? false : 'x'}
        // Actions sit on the trailing edge, so the swipe runs the other way in RTL.
        dragConstraints={isRTL ? { left: 0, right: 148 } : { left: -148, right: 0 }}
        dragElastic={0.1}
        dragMomentum={false}
        onDragStart={() => {
          swipeClickGuard.onDragStart();
          dragOrigin.current = isOpen ? revealOffset : 0;
        }}
        onDragEnd={(_event, info) => {
          if (!isDeleting) onSwipe(dragOrigin.current + info.offset.x);
        }}
        onKeyDown={(event) => {
          if (event.key === (isRTL ? 'ArrowRight' : 'ArrowLeft')) {
            event.preventDefault();
            onSwipe(revealOffset);
          }
        }}
        animate={{ x: isOpen ? revealOffset : 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="relative z-10 w-full bg-white rounded-2xl text-start cursor-grab active:cursor-grabbing select-none"
        style={{ touchAction: 'pan-y', userSelect: 'none', WebkitUserSelect: 'none' }}
        onClick={() => {
          if (isOpen) onSwipe(0);
          else onSelect();
        }}
      >
        <div className="flex items-center px-4 py-4">
          <div className="flex-1 min-w-0">
            <p dir="auto" className="text-[15px] font-medium text-[#434343] truncate">
              {item.original_filename || item.filename}
            </p>
              <p className="text-[13px] text-[#434343]/60 mt-0.5">
              {item.page_count ? `${item.page_count} ${pagesLabel}` : item.file_type?.toUpperCase() || ''}
              </p>
          </div>
          <div className="ms-2 flex-shrink-0">
            <svg
              className="w-2 h-4 text-[#434343]/30"
              fill="currentColor"
              viewBox="0 0 8 13"
            >
              <path d="M1.5 0.5L7 6.5L1.5 12.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      </motion.button>
      {/* Action Buttons (Hidden Behind) */}
      <div id={actionsId} aria-hidden={!isOpen} style={{ visibility: isOpen ? 'visible' : 'hidden' }} className={`absolute end-0 top-0 bottom-0 flex ${isOpen ? '' : 'pointer-events-none'}`}>
        <button
          type="button"
          tabIndex={isOpen ? 0 : -1}
          disabled={!isOpen || isDeleting}
          onClick={(e) => {
            e.stopPropagation();
            onInfo();
          }}
          aria-label={t("common.info")}
          className="w-[74px] bg-[#aeaeb2] flex items-center justify-center active:bg-[#9e9ea2] transition-colors"
        >
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <Info className="h-5 w-5 text-white" />
          </div>
        </button>
        <button
          type="button"
          tabIndex={isOpen ? 0 : -1}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          aria-label={t("common.delete")}
          disabled={!isOpen || isDeleting}
          className="w-[74px] bg-[#ff3b30] flex items-center justify-center active:bg-[#e53429] transition-colors disabled:opacity-50"
        >
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            {isDeleting ? (
              <Loader2 className="h-5 w-5 text-white animate-spin" />
            ) : (
            <Trash2 className="h-5 w-5 text-white" />
            )}
          </div>
        </button>
      </div>

    </div>
  );
}
